import { supabase } from '@/integrations/supabase/client';
import { MODULE_ACCESS, PLAN_FEATURES } from '@/constants/moduleAccess';

export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  billing_interval: string;
  features: {
    modules: string[];
    max_collaborators: number;
    dedicated_support: boolean;
    more_collaborators_on_demand: boolean;
    yearly_discount?: number;
    discount_percentage?: number;
  };
  is_active: boolean;
}

export interface UserSubscription {
  id: string;
  user_id: string;
  plan_id: string;
  status: 'active' | 'inactive' | 'cancelled' | 'trial';
  start_date: string;
  end_date?: string;
  trial_ends_at?: string;
  payment_gateway?: string;
  gateway_subscription_id?: string;
  user_email?: string;
  user_name?: string;
  subscription_plans?: SubscriptionPlan;
}

export interface AdminUser {
  id: string;
  user_id: string;
  email: string;
  role: string;
  permissions: any;
  is_super_admin: boolean;
}

class SubscriptionService {
  private static instance: SubscriptionService;
  private currentSubscription: UserSubscription | null = null;
  private currentPlan: SubscriptionPlan | null = null;
  private isSuperAdmin: boolean | null = null;
  private adminUser: AdminUser | null = null;
  private cachedUserId: string | null = null;
  private cacheExpiry: number = 10 * 60 * 1000; // 10 minutes - increased for better performance
  private lastFetch: number = 0;

  private constructor() {}

  private normalizeModuleName(moduleName: string): string {
    const moduleValue = MODULE_ACCESS[moduleName as keyof typeof MODULE_ACCESS];
    if (moduleValue) {
      return moduleValue;
    }
    return moduleName.toLowerCase();
  }

  public static getInstance(): SubscriptionService {
    if (!SubscriptionService.instance) {
      SubscriptionService.instance = new SubscriptionService();
    }
    return SubscriptionService.instance;
  }

  /**
   * Get the current user's subscription data
   */
  private async fetchCurrentSubscription(): Promise<UserSubscription | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data, error } = await supabase
        .from('user_subscriptions')
        .select(`
          *,
          subscription_plans (*)
        `)
        .eq('user_id', user.id)
        .in('status', ['active', 'trial'])
        .single();

      if (error) {
        console.warn('Error fetching subscription:', error);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Error fetching subscription:', error);
      return null;
    }
  }

  /**
   * Check if user is SuperAdmin
   */
  private async checkSuperAdminStatus(): Promise<{ isSuperAdmin: boolean; adminUser: AdminUser | null }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return { isSuperAdmin: false, adminUser: null };

      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_super_admin', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking super admin status:', error);
        return { isSuperAdmin: false, adminUser: null };
      }

      return {
        isSuperAdmin: !!data,
        adminUser: data || null
      };
    } catch (error) {
      console.error('Error checking super admin status:', error);
      return { isSuperAdmin: false, adminUser: null };
    }
  }

  /**
   * Get current subscription with caching
   */
  private async getCurrentSubscription(): Promise<UserSubscription | null> {
    const { data: { user } } = await supabase.auth.getUser();
    const currentUserId = user?.id || null;

    // Clear cache when auth user changes to avoid plan leakage across sessions.
    if (this.cachedUserId !== currentUserId) {
      this.clearCache();
      this.cachedUserId = currentUserId;
    }

    const now = Date.now();
    
    // Return cached data if still valid
    if (this.currentSubscription && (now - this.lastFetch) < this.cacheExpiry) {
      return this.currentSubscription;
    }

    // Fetch fresh data in parallel for better performance
    const [subscription, superAdminResult] = await Promise.all([
      this.fetchCurrentSubscription(),
      this.checkSuperAdminStatus()
    ]);

    this.currentSubscription = subscription;
    this.currentPlan = subscription?.subscription_plans || null;
    this.isSuperAdmin = superAdminResult.isSuperAdmin;
    this.adminUser = superAdminResult.adminUser;
    this.lastFetch = now;

    return subscription;
  }

  /**
   * Get the current plan name
   */
  public async getPlanName(): Promise<string> {
    const subscription = await this.getCurrentSubscription();
    
    // SuperAdmin gets unlimited access
    if (this.isSuperAdmin) {
      return 'SuperAdmin';
    }

    return subscription?.subscription_plans?.name || 'Basic';
  }

  /**
   * Get the current plan object
   */
  public async getCurrentPlan(): Promise<SubscriptionPlan | null> {
    await this.getCurrentSubscription();
    return this.currentPlan;
  }

  /**
   * Check if user has access to a specific module
   */
  public async hasModuleAccess(moduleName: string): Promise<boolean> {
    const normalizedModuleName = this.normalizeModuleName(moduleName);

    // SuperAdmin has access to everything
    if (this.isSuperAdmin) {
      return true;
    }

    const subscription = await this.getCurrentSubscription();
    if (!subscription?.subscription_plans) {
      return PLAN_FEATURES.BASIC.modules.includes(normalizedModuleName);
    }

    // Check if subscription has trial status and is expired
    if (subscription.status === 'trial') {
      const trialExpired = subscription.trial_ends_at && new Date(subscription.trial_ends_at) < new Date();
      if (trialExpired) {
        return PLAN_FEATURES.BASIC.modules.includes(normalizedModuleName);
      }
    }

    // Prefer canonical plan rules by plan name to avoid mismatches with stale DB feature payloads.
    const normalizedPlanName = subscription.subscription_plans.name?.toUpperCase() as keyof typeof PLAN_FEATURES;
    if (normalizedPlanName && PLAN_FEATURES[normalizedPlanName]) {
      return PLAN_FEATURES[normalizedPlanName].modules.includes(normalizedModuleName);
    }

    const planFeatures = subscription.subscription_plans.features;
    if (!planFeatures?.modules) {
      return false;
    }

    return planFeatures.modules.includes(normalizedModuleName);
  }

  /**
   * Check if user can perform actions in a module
   */
  public async canPerformAction(moduleName: string): Promise<boolean> {
    // SuperAdmin can perform all actions
    if (this.isSuperAdmin) {
      return true;
    }

    // Check if user has module access first
    const hasAccess = await this.hasModuleAccess(moduleName);
    if (!hasAccess) {
      return false;
    }

    // For now, if they have module access, they can perform actions
    // This can be extended with more granular permissions later
    return true;
  }

  /**
   * Check if a module requires an upgrade
   */
  public async requiresUpgrade(moduleName: string): Promise<boolean> {
    // SuperAdmin never needs upgrades
    if (this.isSuperAdmin) {
      return false;
    }

    const hasAccess = await this.hasModuleAccess(moduleName);
    return !hasAccess;
  }

  /**
   * Get upgrade recommendation for a module
   */
  public async getUpgradeRecommendation(moduleName: string): Promise<string> {
    const normalizedModuleName = this.normalizeModuleName(moduleName);

    // SuperAdmin doesn't need upgrades
    if (this.isSuperAdmin) {
      return 'SuperAdmin';
    }

    // Check which plan provides access to this module
    if (PLAN_FEATURES.BASIC.modules.includes(normalizedModuleName)) {
      return 'Basic';
    } else if (PLAN_FEATURES.INDIVIDUAL.modules.includes(normalizedModuleName)) {
      return 'Individual';
    } else if (PLAN_FEATURES.CORPORATE.modules.includes(normalizedModuleName)) {
      return 'Corporate';
    } else if (PLAN_FEATURES.ENTERPRISE.modules.includes(normalizedModuleName)) {
      return 'Enterprise';
    }

    // Default to Enterprise for unknown modules
    return 'Enterprise';
  }

  /**
   * Get maximum collaborators for current plan
   */
  public async getMaxCollaborators(): Promise<number> {
    // SuperAdmin has unlimited collaborators
    if (this.isSuperAdmin) {
      return -1; // Unlimited
    }

    const subscription = await this.getCurrentSubscription();
    if (!subscription?.subscription_plans?.features) {
      return PLAN_FEATURES.BASIC.max_collaborators;
    }

    return subscription.subscription_plans.features.max_collaborators || 1;
  }

  /**
   * Check if user has dedicated support
   */
  public async hasDedicatedSupport(): Promise<boolean> {
    // SuperAdmin has dedicated support
    if (this.isSuperAdmin) {
      return true;
    }

    const subscription = await this.getCurrentSubscription();
    return subscription?.subscription_plans?.features?.dedicated_support || false;
  }

  /**
   * Check if user can request more collaborators on demand
   */
  public async canRequestMoreCollaborators(): Promise<boolean> {
    // SuperAdmin can always request more
    if (this.isSuperAdmin) {
      return true;
    }

    const subscription = await this.getCurrentSubscription();
    return subscription?.subscription_plans?.features?.more_collaborators_on_demand || false;
  }

  /**
   * Get support level for current plan
   */
  public async getSupportLevel(): Promise<string> {
    // SuperAdmin gets dedicated support
    if (this.isSuperAdmin) {
      return 'dedicated';
    }

    const currentPlanName = await this.getPlanName();
    
    switch (currentPlanName.toLowerCase()) {
      case 'basic':
        return 'email';
      case 'individual':
        return 'email';
      case 'corporate':
        return 'priority';
      case 'enterprise':
        return 'dedicated';
      default:
        return 'email';
    }
  }

  /**
   * Check if user is SuperAdmin
   */
  public async isUserSuperAdmin(): Promise<boolean> {
    await this.getCurrentSubscription();
    return this.isSuperAdmin || false;
  }

  /**
   * Get SuperAdmin user data
   */
  public async getSuperAdminUser(): Promise<AdminUser | null> {
    await this.getCurrentSubscription();
    return this.adminUser;
  }

  /**
   * Clear the cache to force fresh data fetch
   */
  public clearCache(): void {
    this.currentSubscription = null;
    this.currentPlan = null;
    this.isSuperAdmin = null;
    this.adminUser = null;
    this.cachedUserId = null;
    this.lastFetch = 0;
  }

  /**
   * Get all available subscription plans
   */
  public async getAvailablePlans(): Promise<SubscriptionPlan[]> {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('price', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching available plans:', error);
      return [];
    }
  }

  /**
   * Update user's subscription plan using the database function
   */
  public async updateSubscription(planId: string, status: 'active' | 'trial' = 'active'): Promise<{ success: boolean; error?: string }> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        return { success: false, error: 'User not authenticated' };
      }

      // Use the database function for better reliability
      const { data, error } = await supabase
        .rpc('upsert_user_subscription', {
          p_user_id: user.id,
          p_plan_id: planId,
          p_status: status
        });

      if (error) {
        console.error('Database function error:', error);
        return { success: false, error: error.message };
      }

      if (data?.error) {
        console.error('Function returned error:', data.error);
        return { success: false, error: data.error };
      }

      console.log('Subscription updated successfully:', data);

      // Clear cache to force fresh data fetch
      this.clearCache();

      return { success: true };
    } catch (error) {
      console.error('Error updating subscription:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to update subscription' 
      };
    }
  }

  /**
   * Get subscription status
   */
  public async getSubscriptionStatus(): Promise<{
    isActive: boolean;
    planName: string;
    status: string;
    startDate: string;
    endDate?: string;
    isTrial: boolean;
    trialEndsAt?: string;
    trialDaysRemaining: number;
  }> {
    const subscription = await this.getCurrentSubscription();
    
    if (!subscription) {
      return {
        isActive: false,
        planName: 'Basic',
        status: 'free',
        startDate: new Date().toISOString(),
        isTrial: false,
        trialDaysRemaining: 0
      };
    }

    const isTrial = subscription.status === 'trial';
    const trialExpired = isTrial && subscription.trial_ends_at && new Date(subscription.trial_ends_at) < new Date();
    const isActive = subscription.status === 'active' || (isTrial && !trialExpired);

    let trialDaysRemaining = 0;
    if (isTrial && subscription.trial_ends_at && !trialExpired) {
      const diffMs = new Date(subscription.trial_ends_at).getTime() - Date.now();
      trialDaysRemaining = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
    }

    return {
      isActive,
      planName: subscription.subscription_plans?.name || 'Unknown',
      status: subscription.status,
      startDate: subscription.start_date,
      endDate: subscription.end_date,
      isTrial,
      trialEndsAt: subscription.trial_ends_at,
      trialDaysRemaining
    };
  }
}

// Export singleton instance
export const subscriptionService = SubscriptionService.getInstance();

// Export the class for testing purposes
export { SubscriptionService }; 
